import { combineReducers, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import FirebaseProjectService from '../../service/Firebase';
import type { IProjectState } from '../../service/ProjectService';
import Project from './Project';

export const submitProject = createAsyncThunk('project/submit', async (_, arg: any) => {
  const { project } = arg.getState();
  const {
    projectBasicInfo,
    projectMs,
    projectTeam,
    acquisitionSchedule,
    capitalizationSchedule,
    procurementSchedule,
    developmentSchedule,
    designSchedule,
    constructionSchedule,
  } = project;

  const srv = new FirebaseProjectService();

  const { id } = await srv.saveProject({
    projectBasicInfo,
    projectMs,
    projectTeam,
    acquisitionSchedule,
    capitalizationSchedule,
    procurementSchedule,
    developmentSchedule,
    designSchedule,
    constructionSchedule,
  });

  return { id };
});

export const fetchProject = createAsyncThunk('project/fetch', async (id: string) => {
  const srv = new FirebaseProjectService();
  const project = await srv.fetchProject(id);

  return { project };
});

export const deleteProject = createAsyncThunk('project/delete', async (id: string) => {
  const srv = new FirebaseProjectService();
  await srv.deleteProject(id);
});

export const listProjects = createAsyncThunk('project/list', async () => {
  const srv = new FirebaseProjectService();
  const projects = await srv.listProjects();
  return projects;
});

const initialState = {
  createdID: '',
  projectBasicInfo: {},
  projectMs: {},
  projectTeam: {},
  acquisitionSchedule: {},
  capitalizationSchedule: {},
  procurementSchedule: {},
  developmentSchedule: {},
  designSchedule: {},
  constructionSchedule: {},
  submitting: false,
  submitSuccess: false,
  submitError: false,
  fetching: false,
  fetchSuccess: false,
  fetchError: false,
  deleting: false,
  deleteSuccess: false,
  deleteError: false,
};

const projectMainSlice = createSlice({
  name: 'projectMain',
  initialState: {
    ...initialState,
    projects: [] as IProjectState[],
  },
  reducers: {
    clear: (state) => ({
      ...state,
      ...{
        submitting: false,
        submitSuccess: false,
        submitError: false,
        fetching: false,
        fetchSuccess: false,
        fetchError: false,
        deleting: false,
        deleteSuccess: false,
        deleteError: false,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(submitProject.pending, (state, _action) => {
      state.submitting = true;
    });
    builder.addCase(submitProject.fulfilled, (state, { payload }) => {
      state.submitSuccess = true;
      state.submitting = false;
      state.createdID = payload.id;
    });
    builder.addCase(submitProject.rejected, (state, { error }) => {
      console.log('submitProject.rejected', error);
      state.submitError = true;
      state.submitting = false;
    });

    builder.addCase(fetchProject.fulfilled, (state, { payload }) => {
      state.fetching = false;
      const { project } = payload;
      state.projectBasicInfo = project.projectBasicInfo;
      state.projectTeam = project.projectTeam;
      state.projectMs = project.projectMs;
      state.acquisitionSchedule = project.acquisitionSchedule;
      state.capitalizationSchedule = project.capitalizationSchedule;
      state.procurementSchedule = project.procurementSchedule;
      state.developmentSchedule = project.developmentSchedule;
      state.designSchedule = project.designSchedule;
      state.constructionSchedule = project.constructionSchedule;
    });
    builder.addCase(fetchProject.pending, (state, { payload }) => {
      state.fetching = true;
    });
    builder.addCase(fetchProject.rejected, (state, { error }) => {
      console.log('fetchProject.rejected', error);
      state.fetching = false;
      state.fetchError = true;
    });

    builder.addCase(listProjects.fulfilled, (state, { payload }) => {
      state.fetching = false;
      state.projects = payload;
    });
    builder.addCase(listProjects.pending, (state, { payload }) => {
      state.fetching = true;
    });
    builder.addCase(listProjects.rejected, (state, { error }) => {
      console.log('listProjects.rejected', error);
      state.fetching = false;
      state.fetchError = true;
    });

    builder.addCase(deleteProject.fulfilled, (state, { payload }) => {
      state.deleting = false;
      state.deleteSuccess = true;
    });
    builder.addCase(deleteProject.pending, (state, { payload }) => {
      state.deleting = true;
    });
    builder.addCase(deleteProject.rejected, (state, { error }) => {
      console.log('deleteProject.rejected', error);
      state.deleting = false;
      state.deleteError = true;
    });
  },
});

export const { clear } = projectMainSlice.actions;

const projectReducer = combineReducers({
  projectMain: projectMainSlice.reducer,
  Project,
});

export default projectReducer;
